/**
 * DualAxisAreaChart — ECharts dual-axis area chart for conversation + token trends.
 *
 * Props:
 * - data: Array<{ date, conversations, tokens, messages? }>
 * - height: number
 * - theme: 'light' | 'dark'
 * - series: Array<{ key, name, color, yAxisIndex }> — configurable series
 */

import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const DEFAULT_SERIES = [
  { key: 'conversations', name: 'Conversations', color: '#2d3a52', yAxisIndex: 0 },
  { key: 'tokens', name: 'Tokens', color: '#0d9488', yAxisIndex: 1 },
];

export default function DualAxisAreaChart({
  data = [],
  height = 300,
  theme = 'light',
  series = DEFAULT_SERIES,
}) {
  const isDark = theme === 'dark';

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
      },
      legend: {
        bottom: 0,
        textStyle: { color: isDark ? 'rgba(228,232,237,0.7)' : '#6b7280', fontSize: 11 },
      },
      grid: { left: 55, right: 55, top: 20, bottom: 40 },
      xAxis: {
        type: 'category',
        data: data.map((d) => d.date),
        boundaryGap: false,
        axisLabel: { color: isDark ? 'rgba(228,232,237,0.5)' : '#6b7280', fontSize: 10 },
        axisLine: { lineStyle: { color: isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb' } },
      },
      yAxis: [
        {
          type: 'value',
          position: 'left',
          axisLabel: { color: isDark ? 'rgba(228,232,237,0.5)' : '#6b7280', fontSize: 10 },
          splitLine: { lineStyle: { color: isDark ? 'rgba(255,255,255,0.04)' : '#f3f4f6' } },
        },
        {
          type: 'value',
          position: 'right',
          axisLabel: {
            color: isDark ? 'rgba(228,232,237,0.5)' : '#6b7280',
            fontSize: 10,
            formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v),
          },
          splitLine: { show: false },
        },
      ],
      series: series.map((s) => ({
        name: s.name,
        type: 'line',
        yAxisIndex: s.yAxisIndex || 0,
        data: data.map((d) => d[s.key] || 0),
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        showSymbol: data.length <= 14,
        lineStyle: { color: s.color, width: 2 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: `${s.color}30` },
              { offset: 1, color: `${s.color}05` },
            ],
          },
        },
        itemStyle: { color: s.color },
      })),
    }),
    [data, series, isDark]
  );

  if (!data.length) return null;

  return (
    <ReactECharts
      option={option}
      style={{ width: '100%', height }}
      opts={{ renderer: 'canvas' }}
      notMerge
    />
  );
}
